import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { viewTransition } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';
import { PaginatedListAspectRatioHelper } from '@theme/paginated-list-aspect-ratio';

/**
 * A custom element that renders a paginated list of items.
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} [grid] - The grid element.
 * @property {HTMLSpanElement} [viewMorePrevious] - The view more previous button.
 * @property {HTMLSpanElement} [viewMoreNext] - The view more next button.
 * @property {HTMLButtonElement} [retryPrevious] - Manual retry after a previous-page request fails.
 * @property {HTMLButtonElement} [retryNext] - Manual retry after a next-page request fails.
 * @property {HTMLElement[]} [cards] - The cards elements.
 *
 * @extends Component<Refs>
 */
export default class PaginatedList extends Component {
  /**
   * @type {Map<number, string>}
   */
  pages = new Map();

  /** @type {IntersectionObserver | undefined} */
  infinityScrollObserver;

  /** @type {Map<string, Promise<boolean>>} */
  #pageFetches = new Map();

  /** Incremented when filters change so stale responses cannot repopulate the grid cache. */
  #generation = 0;

  #renderingNext = false;

  #renderingPrevious = false;

  /** @type {PaginatedListAspectRatioHelper} */
  #aspectRatioHelper;

  connectedCallback() {
    super.connectedCallback();

    /** @type {HTMLElement | null} */
    const templateCard = this.querySelector('[ref="cardGallery"]');
    if (templateCard) {
      this.#aspectRatioHelper = new PaginatedListAspectRatioHelper({
        templateCard,
      });
    }

    this.#prioritizeFirstImage();
    this.#observeViewMore();

    // Listen for filter updates to clear cached pages
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
    document.addEventListener(ThemeEvents.FilterRendered, this.#handleFilterRendered);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.infinityScrollObserver) {
      this.infinityScrollObserver.disconnect();
    }
    this.#generation += 1;
    this.pages.clear();
    this.#pageFetches.clear();
    // Remove the filter update listener
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
    document.removeEventListener(ThemeEvents.FilterRendered, this.#handleFilterRendered);
  }

  #observeViewMore() {
    const { viewMorePrevious, viewMoreNext } = this.refs;

    // Return if neither element exists
    if (!viewMorePrevious && !viewMoreNext) return;

    // Create observer if it doesn't exist
    if (!this.infinityScrollObserver) {
      this.infinityScrollObserver = new IntersectionObserver(
        async (entries) => {
          // Wait for any in-progress view transitions to finish
          if (viewTransition.current) await viewTransition.current;

          for (const entry of entries) {
            if (entry.isIntersecting) {
              // Use current refs to check which element triggered
              const { viewMorePrevious, viewMoreNext } = this.refs;

              if (entry.target === viewMorePrevious) {
                void this.#renderPreviousPage();
              } else if (entry.target === viewMoreNext) {
                void this.#renderNextPage();
              }
            }
          }
        },
        {
          // Start loading shortly before the customer reaches the sentinel,
          // without fetching another full product page during initial load.
          rootMargin: '400px 0px',
        }
      );
    }

    // Observe the view more elements
    if (viewMorePrevious) {
      this.infinityScrollObserver.observe(viewMorePrevious);
    }

    if (viewMoreNext) {
      this.infinityScrollObserver.observe(viewMoreNext);
    }
  }

  /**
   * @param {{ page: number, url?: URL } | undefined} pageInfo - The page info
   * @returns {boolean} Whether to use the page
   */
  #shouldUsePage(pageInfo) {
    if (!pageInfo) return false;

    const { grid } = this.refs;
    const lastPage = grid?.dataset.lastPage;

    if (!lastPage || pageInfo.page < 1 || pageInfo.page > Number(lastPage)) return false;

    return true;
  }

  /**
   * @param {number} pageNumber - The page number to fetch
   * @param {URL} [url] - Optional URL, will be constructed if not provided
   */
  async #fetchSpecificPage(pageNumber, url = undefined) {
    if (this.pages.has(pageNumber)) return true;

    const resolvedUrl = url ?? new URL(window.location.href);
    if (!url) resolvedUrl.searchParams.set('page', pageNumber.toString());
    resolvedUrl.hash = '';

    const pageInfo = { page: pageNumber, url: resolvedUrl };

    if (!this.#shouldUsePage(pageInfo)) return false;

    const generation = this.#generation;
    const requestKey = `${generation}:${resolvedUrl.toString()}`;
    const pendingRequest = this.#pageFetches.get(requestKey);
    if (pendingRequest) return pendingRequest;

    const request = (async () => {
      try {
        const pageContent = await sectionRenderer.getSectionHTML(this.sectionId, true, resolvedUrl);

        if (!this.isConnected || generation !== this.#generation) return false;
        this.pages.set(pageNumber, pageContent);
        return true;
      } catch (error) {
        console.warn(`Unable to load collection page ${pageNumber}`, error);
        return false;
      } finally {
        this.#pageFetches.delete(requestKey);
      }
    })();

    this.#pageFetches.set(requestKey, request);
    return request;
  }

  async #renderNextPage() {
    if (this.#renderingNext) return;
    this.#renderingNext = true;
    this.#setRetryVisibility('next', false);

    const { grid } = this.refs;

    if (!grid) {
      this.#renderingNext = false;
      return;
    }

    try {
      const nextPage = this.#getPage('next');

      if (!nextPage || !this.#shouldUsePage(nextPage)) return;
      let nextPageItemElements = this.#getGridForPage(nextPage.page);

      if (!nextPageItemElements) {
        const fetched = await this.#fetchSpecificPage(nextPage.page, nextPage.url);
        if (!fetched) {
          this.#setRetryVisibility('next', true);
          return;
        }

        nextPageItemElements = this.#getGridForPage(nextPage.page);
      }

      if (!nextPageItemElements?.length) {
        this.#setRetryVisibility('next', true);
        return;
      }

      grid.append(...nextPageItemElements);

      this.#aspectRatioHelper?.processNewElements();

      // Infinite scrolling should not add one browser-history entry per page.
      window.history.replaceState(window.history.state, '', nextPage.url.toString());
      this.#observeViewMore();

    } catch (error) {
      console.warn('Unable to render the next collection page', error);
      this.#setRetryVisibility('next', true);
    } finally {
      this.#renderingNext = false;
    }
  }

  async #renderPreviousPage() {
    if (this.#renderingPrevious) return;
    this.#renderingPrevious = true;
    this.#setRetryVisibility('previous', false);

    const { grid } = this.refs;

    if (!grid) {
      this.#renderingPrevious = false;
      return;
    }

    try {
      const previousPage = this.#getPage('previous');
      if (!previousPage || !this.#shouldUsePage(previousPage)) return;

      let previousPageItemElements = this.#getGridForPage(previousPage.page);
      if (!previousPageItemElements) {
        const fetched = await this.#fetchSpecificPage(previousPage.page, previousPage.url);
        if (!fetched) {
          this.#setRetryVisibility('previous', true);
          return;
        }

        previousPageItemElements = this.#getGridForPage(previousPage.page);
      }

      if (!previousPageItemElements?.length) {
        this.#setRetryVisibility('previous', true);
        return;
      }

      // Store the current scroll position and height of the first element
      const scrollTop = window.scrollY;
      const firstElement = grid.firstElementChild;
      const oldHeight = firstElement ? firstElement.getBoundingClientRect().top + window.scrollY : 0;

      // Prepend the new elements
      grid.prepend(...previousPageItemElements);

      this.#aspectRatioHelper?.processNewElements();

      window.history.replaceState(window.history.state, '', previousPage.url.toString());
      this.#observeViewMore();

      // Calculate and adjust scroll position to maintain the same view
      if (firstElement) {
        const newHeight = firstElement.getBoundingClientRect().top + window.scrollY;
        const heightDiff = newHeight - oldHeight;
        window.scrollTo({
          top: scrollTop + heightDiff,
          behavior: 'auto',
        });
      }

    } catch (error) {
      console.warn('Unable to render the previous collection page', error);
      this.#setRetryVisibility('previous', true);
    } finally {
      this.#renderingPrevious = false;
    }
  }

  /**
   * @param {"previous" | "next"} type
   * @returns {{ page: number, url: URL } | undefined}
   */
  #getPage(type) {
    const { cards } = this.refs;
    const isPrevious = type === 'previous';

    if (!Array.isArray(cards)) return;

    const targetCard = cards[isPrevious ? 0 : cards.length - 1];

    if (!targetCard) return;

    const currentCardPage = Number(targetCard.dataset.page);
    const page = isPrevious ? currentCardPage - 1 : currentCardPage + 1;

    const url = new URL(window.location.href);
    url.searchParams.set('page', page.toString());
    url.hash = '';

    return {
      page,
      url,
    };
  }

  /**
   * @param {number} page
   * @returns {Element[] | undefined}
   */
  #getGridForPage(page) {
    const pageHTML = this.pages.get(page);

    if (!pageHTML) return;

    const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
    const gridElement = parsedPage.querySelector('[ref="grid"]');
    if (!gridElement) return;

    const items = Array.from(gridElement.querySelectorAll(':scope > [ref="cards[]"]'));

    // On mobile, cards appended after the first page use one reliable image.
    // This keeps the initial cards swipeable while preventing every subsequent
    // page from creating dozens of horizontal scrollers and image decoders.
    if (page > 1 && window.matchMedia('(max-width: 749px)').matches) {
      for (const item of items) {
        const slideshow = item.querySelector('slideshow-component');
        if (!slideshow) continue;

        const slides = Array.from(slideshow.querySelectorAll('slideshow-slide'));
        const firstSlide = slides.find((slide) => !slide.hasAttribute('hidden')) ?? slides[0];

        for (const slide of slides) {
          if (slide !== firstSlide) slide.remove();
        }

        slideshow.querySelectorAll('slideshow-arrows, slideshow-controls').forEach((control) => control.remove());
        slideshow.setAttribute('disabled', 'true');
        slideshow.setAttribute('performance-static-gallery', '');
      }
    }

    // The detached elements are now the cache. Do not also retain the complete
    // section response in this component after it has been parsed.
    this.pages.delete(page);
    return items;
  }

  /** Retry a failed next-page request from the fallback button. */
  retryNextPage = () => void this.#renderNextPage();

  /** Retry a failed previous-page request from the fallback button. */
  retryPreviousPage = () => void this.#renderPreviousPage();

  /**
   * @param {'previous' | 'next'} type
   * @param {boolean} visible
   */
  #setRetryVisibility(type, visible) {
    const retryButton = type === 'next' ? this.refs.retryNext : this.refs.retryPrevious;
    if (retryButton instanceof HTMLButtonElement) retryButton.hidden = !visible;

    if (visible) {
      const sentinel = type === 'next' ? this.refs.viewMoreNext : this.refs.viewMorePrevious;
      if (sentinel) this.infinityScrollObserver?.unobserve(sentinel);
    }
  }

  #prioritizeFirstImage() {
    const firstImage = this.refs.grid?.querySelector(':scope > [ref="cards[]"] img');
    if (!(firstImage instanceof HTMLImageElement)) return;

    firstImage.loading = 'eager';
    firstImage.removeAttribute('loading');
    firstImage.fetchPriority = 'high';
    firstImage.setAttribute('fetchpriority', 'high');
  }

  get sectionId() {
    const id = this.getAttribute('section-id');

    if (!id) throw new Error('The section-id attribute is required');

    return id;
  }

  /**
   * Handle filter updates by clearing cached pages
   */
  #handleFilterUpdate = () => {
    this.#generation += 1;
    this.pages.clear();
    this.infinityScrollObserver?.disconnect();
    this.#setRetryVisibility('next', false);
    this.#setRetryVisibility('previous', false);
  };

  /**
   * Resume pagination only after the filtered section has completed (or the
   * URL has been restored after a failure).
   * @param {Event} event
   */
  #handleFilterRendered = (event) => {
    const sectionId = event instanceof CustomEvent ? event.detail?.sectionId : undefined;
    if (sectionId !== this.sectionId || !this.isConnected) return;

    this.#prioritizeFirstImage();
    this.#observeViewMore();
  };
}
